import type { ObjectId } from '@freightview/object-id'

import type { MarkupSettings } from './account-user.ts'
import type { LoadBidExpiration } from './bid-expiration.ts'
import type { CarrierTrackingDTO } from './carrier-tracking.ts'
import type {
  Currency,
  DistanceUOM,
  ShipmentDirection,
  Workflow,
} from './common.ts'
import type { LoadCustomsBroker } from './customs-broker.ts'
import type { DeliveryOption } from './delivery-option.ts'
import type { LoadExchangeRates } from './exchange-rate.ts'
import type { HoldAtLocation } from './hold-at-location.ts'
import type { LoadBillTo } from './load-bill-to.ts'
import type { LoadBOLDetail } from './load-bol.ts'
import type { LoadBrand } from './load-brand.ts'
import type { LoadCancelDetail } from './load-cancel-detail.ts'
import type { DispatchWarning } from './load-dispatch.ts'
import type { LoadDocumentDTO } from './load-document.ts'
import type { LoadEmergencyContact } from './load-emergency-contact.ts'
import type { LoadEquipment } from './load-equipment.ts'
import type { LoadItem } from './load-item.ts'
import type { LoadLocation } from './load-location.ts'
import type { LoadPickupDetail } from './load-pickup.ts'
import type { LoadSaltDetails } from './load-salt.ts'
import type { LoadShare } from './load-share.ts'
import type { FullLoadTag } from './load-tag.ts'
import type { LoadUploadReference } from './load-upload.ts'
import type { LocationAccessorialDTO } from './location-accessorial.ts'
import type { MessageSummary } from './message.ts'
import type { MissedPickup } from './missed-pickup.ts'
import type { QuoteDTO, SelectedQuote } from './quote.ts'
import type { QuoteSelectionReason } from './quote-selection-reason.ts'
import type { LoadRefNum } from './ref-num.ts'
import type { RouteGuideLoadStatus } from './route-guide.ts'
import type { TrackDetail, TrackingStatus } from './track.ts'
import type { UserInfo } from './user-info.ts'

export const shipmentStatuses = [
  'pending',
  'awarded',
  'confirmed',
  'picked-up',
  'out-for-delivery',
  'delivered',
  'canceled',
] as const
export type ShipmentStatus = (typeof shipmentStatuses)[number]

export const loadSources = [
  'legacy',
  'app',
  'v1',
  'v2',
  'upload',
  'chat',
] as const
export type LoadSource = (typeof loadSources)[number]

export type BookSource = 'v1' | 'v2' | 'app' | 'bulk'
export type QuoteProcessStatus =
  | 'ok' // Finished rating or finished rerating with no further action needed by shipper
  | 'requesting' // Currently fetching quotes from hydra/contracted rates repo
  | 'rerate-ready' // Found a comparible quote, but price is different
  | 'rerating' // Show overlay
  | 'rerate-no-compat' // Rerated and no comparible quotes found
export interface LoadCreatedBy {
  userId: string
  accountId: string
  company: string
  name?: string
  email?: string
  adminUserId?: string
  vendorId?: string
  markup?: MarkupSettings
}
export interface LoadBookedBy extends UserInfo {
  vendorId?: string
  source?: BookSource
}

export interface ShipperShipment {
  loadId: string
  status: ShipmentStatus
  canceledByUserId?: string
  accountId: string
  quotedByUserId: string
  quotedByAdminUserId?: string
  newMessageCount: number
  tracking?: TrackDetail

  cheapestQuote?: QuoteDTO
  selectedQuote?: SelectedQuote

  messageSummary?: MessageSummary
  createdDate: Date

  hasRecipients?: boolean
  estimatedDeliveryDate?: Date
  targetPrice?: number
  targetPriceCurrency?: Currency

  _id: ObjectId
  billTo?: LoadBillTo
  bol: LoadBOLDetail
  bookedBy?: LoadBookedBy
  bookedDate?: Date
  createdBy?: LoadCreatedBy
  currentQuoteVersion?: number
  direction: ShipmentDirection
  distance?: number
  distanceUOM?: DistanceUOM
  documents: LoadDocumentDTO[]
  equipment: LoadEquipment
  isArchived: boolean
  isLegacy?: boolean
  isLiveLoad: boolean
  isMultiTruck: boolean
  items: LoadItem[]
  glCode?: string
  templateId?: ObjectId
  lastUpdatedDate?: Date
  locations: LoadLocation[]
  pickup: LoadPickupDetail
  pickupDate: Date
  quoteProcessStatus?: QuoteProcessStatus
  quoteSelectionReason?: QuoteSelectionReason
  quoteRequestId: string
  refNums: LoadRefNum[]
  routeGuide?: {
    status?: RouteGuideLoadStatus
    /** Booking validation errors that kept the route guide from running. */
    blockers?: string[]
  }
  shares?: LoadShare[]
  source: LoadSource
  workflow: Workflow
  _m?: number
  _v?: number
  bidExpiration?: LoadBidExpiration
  exchangeRates?: LoadExchangeRates
  isFreightCollect?: boolean
  vendor?: ShipmentVendor
  warnings?: DispatchWarning[]
  brand?: LoadBrand
  upload?: LoadUploadReference
  saltDetails?: LoadSaltDetails
}

export interface ShipmentVendor {
  name: string
  quoteAmount?: number
}

export interface CalendarShipmentSummary {
  loadId: string
  pickupDate: Date
  pickupDateActual?: Date
  proNumber?: string
  bolNumber: string
  trackingStatus?: TrackingStatus
  deliveryDate?: Date
  pickupAppt?: LocationAccessorialDTO
  deliveryAppt?: LocationAccessorialDTO
}

export interface FullShipperShipment extends ShipperShipment {
  customsBroker?: LoadCustomsBroker[]
  emergencyContact?: LoadEmergencyContact
  tags?: FullLoadTag[]
  cancel?: Pick<LoadCancelDetail, 'canceledByUserId' | 'status' | 'mode'> & {
    errorMessage: string
  }
  carrierTracking?: CarrierTrackingDTO | null
  holdAtLocation?: HoldAtLocation
  deliveryOption?: DeliveryOption
  schedulePickup: boolean
  missedPickup?: MissedPickup
}

export const isHazardous = (
  load: Pick<ShipperShipment, 'items' | 'equipment'>,
) => {
  if (load.items?.length) {
    return load.items.some(
      i => i.hazardous?.length || i.contains?.some(c => c.hazardous?.length),
    )
  }

  return load.equipment.isHazardous ?? false
}
